import React, { useMemo, useState } from "react";
import { LightForm } from "../../../light/types";
import { ShapePickerIcon } from "../../../components/light/ShapePickerIcons";

interface ModalSelectionFormeLightProps {
  isOpen: boolean;
  forms: LightForm[];
  productType?: "coussin" | "bache";
  onSelect: (id: string) => void;
  onClose: () => void;
}

const ICONS_BY_FORM: Record<string, string> = {
  rectangulaire_bandeau: "🟢  🔧  🪑",
  rectangulaire_joues: "🟠  🔧  🛋️",
  rectangulaire_demi_bandeau: "🟢  🔧  🪑",
  rectangle_demi_bandeau_biseau: "🔴  🛠️  🔩",
  rectangle_joues_biseau: "🔴  🛠️  🔩",
  rectangle_boite: "🟠  🛠️  🛋️",
  rond_bandeau: "🟠  🔧  🛋️",
  rond_avec_bandeau: "🟠  🔧  🛋️",
  cylindre_joues: "🔴  🛠️  🛋️",
  rectangle_simple: "🟢  🛠️  ☂️",
};

const INFO_BY_FORM: Record<string, { title: string; tips: string[] }> = {
  rectangulaire_bandeau: {
    title: "Rectangulaire bandeau",
    tips: ["Couture droite tout du long", "Bandeau à poser entre les deux plateaux", "Coins faciles à aligner", "Convient aux débutants"],
  },
  rectangulaire_joues: {
    title: "Rectangulaire avec joues",
    tips: ["Joues = parois verticales", "Deux longues coutures + 2 petites", "Coins à bien marquer au fer", "Rendu très propre et régulier"],
  },
  rectangulaire_demi_bandeau: {
    title: "Demi-bandeau",
    tips: ["Montage rapide", "Un seul côté vertical", "Très simple à piquer", "Idéal premiers projets"],
  },
  rectangle_demi_bandeau_biseau: {
    title: "Demi-bandeau biseauté",
    tips: ["Même montage mais avec angle", "Tracer la pente pour éviter les décalages", "Maintenir avec pinces plutôt qu’épingles", "Niveau intermédiaire"],
  },
  rectangle_joues_biseau: {
    title: "Joues biseautées",
    tips: ["Joues inclinées", "Prévoir des repères pour l’orientation", "Piquage précis recommandé", "Rendu très ergonomique"],
  },
  rectangle_boite: {
    title: "Rectangle boîte",
    tips: ["Quatre coutures d’angles à 90°", "Bien marquer les coins au fer", "Structure rigide", "Effet “boîte” très propre"],
  },
  rond_bandeau: {
    title: "Rond avec bandeau",
    tips: ["Couture circulaire : aller lentement", "Épingler ou clipper tous les 3–4 cm", "Très bon exercice de couture en courbe", "Beau fini arrondi"],
  },
  rond_avec_bandeau: {
    title: "Rond avec bandeau",
    tips: ["Couture circulaire : aller lentement", "Épingler ou clipper tous les 3–4 cm", "Très bon exercice de couture en courbe", "Beau fini arrondi"],
  },
  cylindre_joues: {
    title: "Cylindre avec joues",
    tips: ["Technicité : coudre en rond puis refermer", "Gérer la tension régulière du tissu", "Super exercice pour progresser", "Idéal rouleaux/poufs"],
  },
  rectangle_simple: {
    title: "Bâche rectangulaire",
    tips: ["Doubler le bord (ourlet) puis piquer", "Poser les œillets aux angles", "Résultat robuste", "Niveau débutant"],
  },
};

// mapping vers les vraies icônes du build Pro (noms de fichier parfois typo/majuscules)
const ICON_MAP: Record<string, string> = {
  rectangulaire_bandeau: "/images/formes/coussins/rectanglulaire_bandeau.png",
  rectangulaire_joues: "/images/formes/coussins/rectanglulaire_joues.png",
  rectangulaire_demi_bandeau: "/images/formes/coussins/rectanglulaire_demi_bandeau.png",
  rectangle_demi_bandeau_biseau: "/images/formes/coussins/rectangle_demi_bandeau_biseau.png",
  rectangle_joues_biseau: "/images/formes/coussins/Rectangle_joues_biseau.png",
  rectangle_boite: "/images/formes/coussins/rectangle_boite.png",
  rond_bandeau: "/images/formes/coussins/rond_avec_bandeau.png",
  rond_avec_bandeau: "/images/formes/coussins/rond_avec_bandeau.png",
  cylindre_joues: "/images/formes/coussins/cylindre_joues.png",
  rectangle_simple: "/images/formes/coussins/rectangle_simple.png",
};

const iconPath = (id: string) => ICON_MAP[id] || `/images/formes/coussins/${id}.png`;

const FormCard: React.FC<{
  form: LightForm;
  onSelect: (id: string) => void;
  onZoom: (id: string) => void;
}> = ({ form, onSelect, onZoom }) => {
  const [broken, setBroken] = useState(false);
  return (
    <button
      key={form.id}
      type="button"
      onClick={() => onSelect(form.id)}
      className="flex flex-col items-center gap-2 p-3 border rounded-lg transition shadow-sm border-gray-200 hover:border-blue-500 hover:shadow"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onZoom(form.id);
        }}
        className="self-end text-xs text-gray-500 hover:text-gray-700"
        aria-label="Voir en grand"
      >
        🔍
      </button>
      {!broken ? (
        <img
          src={iconPath(form.id)}
          alt={form.nom || form.id}
          className="w-28 h-20 object-contain"
          onError={() => setBroken(true)}
        />
      ) : (
        <ShapePickerIcon id={form.id} label="" selectedId={undefined} onSelect={() => {}} />
      )}
      <span className="text-sm font-medium text-gray-800 text-center">{form.nom || form.id}</span>
      <div className="text-sm flex justify-center gap-1 mt-1">{ICONS_BY_FORM[form.id] || ""}</div>
    </button>
  );
};

export const ModalSelectionFormeLight: React.FC<ModalSelectionFormeLightProps> = ({
  isOpen,
  forms,
  productType,
  onSelect,
  onClose,
}) => {
  const [zoomFormId, setZoomFormId] = useState<string | null>(null);
  const filtered = useMemo(() => {
    return forms.filter((f) => {
      const tags = (f.tagsUsage || []).map((t) => t.toLowerCase());
      const cat = (f.categorie || "").toLowerCase();
      if (!productType) return true;
      if (productType === "bache") {
        return tags.includes("bache") || cat.includes("bache") || f.id.includes("bache") || f.id.includes("rectangle");
      }
      return tags.includes("coussin") || cat.includes("coussin") || !tags.length;
    });
  }, [forms, productType]);

  const zoomForm = useMemo(() => (zoomFormId ? filtered.find((f) => f.id === zoomFormId) : null), [zoomFormId, filtered]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[80vh] overflow-auto space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Choisissez une forme</h3>
          <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Fermer
          </button>
        </div>

        {filtered.length === 0 && <div className="text-sm text-gray-500">Aucune forme disponible.</div>}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {filtered.map((f) => (
            <FormCard
              key={f.id}
              form={f}
              onZoom={(id) => setZoomFormId(id)}
              onSelect={(id) => {
                onSelect(id);
                onClose();
              }}
            />
          ))}
        </div>
        <div className="text-xs text-gray-500 text-center mt-4">
          🟢/🟠/🔴 difficulté • 🔧/🛠️ solidité • 🪑/🛋️/🎒/☂️/🔩 usage
        </div>

        {zoomForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-auto space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold text-gray-900">{zoomForm.nom || zoomForm.id}</h4>
                <button
                  type="button"
                  onClick={() => setZoomFormId(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Fermer
                </button>
              </div>
              <div className="flex justify-center">
                <img
                  src={iconPath(zoomForm.id)}
                  alt={zoomForm.nom || zoomForm.id}
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </div>
              {INFO_BY_FORM[zoomForm.id] && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-gray-900">{INFO_BY_FORM[zoomForm.id].title}</div>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {INFO_BY_FORM[zoomForm.id].tips.map((tip) => (
                      <li key={tip}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs text-gray-500 text-center">
                🟢/🟠/🔴 difficulté • 🔧/🛠️ solidité • 🪑/🛋️/🎒/☂️/🔩 usage
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalSelectionFormeLight;
